import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { ThemeProvider } from '@/providers/ThemeProvider'
import { AppLayout } from '@/components/layout/AppLayout'
import { AuthLayout } from '@/components/layout/AuthLayout'
import { PageLoader } from '@/components/ui/PageLoader'
import { Toaster } from '@/components/ui/Toaster'

// ── Auth pages ─────────────────────────────────────────────────────────────────
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'))
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'))
const TwoFactorVerifyPage = lazy(() => import('@/pages/auth/TwoFactorVerifyPage'))

// ── App pages (lazy-loaded for code splitting) ────────────────────────────────
const DashboardPage = lazy(() => import('@/pages/DashboardPage'))
const LiveMapPage = lazy(() => import('@/pages/LiveMapPage'))

// Tracking
const VehiclesPage = lazy(() => import('@/pages/tracking/VehiclesPage'))
const VehicleDetailPage = lazy(() => import('@/pages/tracking/VehicleDetailPage'))
const DevicesPage = lazy(() => import('@/pages/tracking/DevicesPage'))
const SimsPage = lazy(() => import('@/pages/tracking/SimsPage'))
const DriversPage = lazy(() => import('@/pages/tracking/DriversPage'))
const GeofencesPage = lazy(() => import('@/pages/tracking/GeofencesPage'))
const TripsPage = lazy(() => import('@/pages/tracking/TripsPage'))
const TripDetailPage = lazy(() => import('@/pages/tracking/TripDetailPage'))
const AlertsPage = lazy(() => import('@/pages/tracking/AlertsPage'))
const PacketInspectorPage = lazy(() => import('@/pages/tracking/PacketInspectorPage'))
const GpsHistoryPage = lazy(() => import('@/pages/tracking/GpsHistoryPage'))
const HistoryPage = lazy(() => import('@/pages/tracking/HistoryPage'))

// School
const SchoolsPage = lazy(() => import('@/pages/school/SchoolsPage'))
const SchoolRoutesPage = lazy(() => import('@/pages/school/RoutesPage'))
const StudentsPage = lazy(() => import('@/pages/school/StudentsPage'))
const SchoolAttendancePage = lazy(() => import('@/pages/school/AttendancePage'))

// Field Force
const EmployeesPage = lazy(() => import('@/pages/fieldforce/EmployeesPage'))
const FieldAttendancePage = lazy(() => import('@/pages/fieldforce/AttendancePage'))
const TasksPage = lazy(() => import('@/pages/fieldforce/TasksPage'))

// Pets
const PetsPage = lazy(() => import('@/pages/PetsPage'))

// EV
const BatteriesPage = lazy(() => import('@/pages/ev/BatteriesPage'))
const BatterySwapsPage = lazy(() => import('@/pages/ev/BatterySwapsPage'))
const EVStationsPage = lazy(() => import('@/pages/ev/StationsPage'))

// Dispatch
const BookingsPage = lazy(() => import('@/pages/dispatch/BookingsPage'))
const DispatchClientsPage = lazy(() => import('@/pages/dispatch/ClientsPage'))
const ServiceTypesPage = lazy(() => import('@/pages/dispatch/ServiceTypesPage'))
const PromosPage = lazy(() => import('@/pages/dispatch/PromosPage'))
const PackagesPage = lazy(() => import('@/pages/dispatch/PackagesPage'))

// Billing
const SubscriptionsPage   = lazy(() => import('@/pages/billing/SubscriptionsPage'))
const MySubscriptionPage  = lazy(() => import('@/pages/billing/MySubscriptionPage'))
const InvoicesPage        = lazy(() => import('@/pages/billing/InvoicesPage'))
const PaymentsPage        = lazy(() => import('@/pages/billing/PaymentsPage'))
const WalletPage          = lazy(() => import('@/pages/billing/WalletPage'))
const PlansPage           = lazy(() => import('@/pages/billing/PlansPage'))
const DomainPlansPage     = lazy(() => import('@/pages/billing/DomainPlansPage'))
const GatewaysPage        = lazy(() => import('@/pages/billing/GatewaysPage'))
const BillingProfilePage  = lazy(() => import('@/pages/billing/BillingProfilePage'))
const PdfTemplatesPage       = lazy(() => import('@/pages/billing/PdfTemplatesPage'))
const TaxConfigPage          = lazy(() => import('@/pages/billing/TaxConfigPage'))
const SigningCertPage        = lazy(() => import('@/pages/billing/SigningCertPage'))
const PlatformProfilePage    = lazy(() => import('@/pages/billing/PlatformProfilePage'))
const PlatformGatewaysPage   = lazy(() => import('@/pages/billing/PlatformGatewaysPage'))
const CustomerBillingPage    = lazy(() => import('@/pages/billing/CustomerBillingPage'))
const DunningRulesPage       = lazy(() => import('@/pages/billing/DunningRulesPage'))

// Reports
const ReportsPage = lazy(() => import('@/pages/ReportsPage'))

// Admin
const AdminUsersPage = lazy(() => import('@/pages/admin/UsersPage'))
const UserTypesPage = lazy(() => import('@/pages/admin/UserTypesPage'))
const AlertTypesPage = lazy(() => import('@/pages/admin/AlertTypesPage'))
const AlertRulesPage = lazy(() => import('@/pages/admin/AlertRulesPage'))
const NotificationsPage = lazy(() => import('@/pages/admin/NotificationsPage'))
const PaymentGatewaysPage = lazy(() => import('@/pages/admin/PaymentGatewaysPage'))
const DomainSettingsPage = lazy(() => import('@/pages/admin/DomainSettingsPage'))
const VehicleTypesPage = lazy(() => import('@/pages/admin/VehicleTypesPage'))
const DataBrokerPage = lazy(() => import('@/pages/admin/DataBrokerPage'))
const ApiKeysPage            = lazy(() => import('@/pages/admin/ApiKeysPage'))
const DashcamConfigPage      = lazy(() => import('@/pages/admin/DashcamConfigPage'))
const DocumentRemindersPage  = lazy(() => import('@/pages/admin/DocumentRemindersPage'))
const CalendarsPage          = lazy(() => import('@/pages/CalendarsPage'))

// Transport
const TransportVendorsPage   = lazy(() => import('@/pages/transport/VendorsPage'))
const TransportEmployeesPage = lazy(() => import('@/pages/transport/EmployeesPage'))
const TransportRoutesPage    = lazy(() => import('@/pages/transport/RoutesPage'))
const TransportRostersPage   = lazy(() => import('@/pages/transport/RostersPage'))
const TransportSOSPage       = lazy(() => import('@/pages/transport/SOSPage'))

// White Label
const WhiteLabelDomainsPage     = lazy(() => import('@/pages/whitelabel/DomainsPage'))
const WhiteLabelMigrationsPage  = lazy(() => import('@/pages/whitelabel/MigrationsPage'))

// System (super admin)
const SystemPage = lazy(() => import('@/pages/system/SystemPage'))
const ParsersPage = lazy(() => import('@/pages/system/ParsersPage'))

// Profile
const ProfilePage = lazy(() => import('@/pages/ProfilePage'))

// Public (no auth required)
const SharedLocationPage = lazy(() => import('@/pages/SharedLocationPage'))

export default function App() {
  return (
    <ThemeProvider>
      <Toaster />
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* ── Public routes (no auth, no sidebar) ─────────────────── */}
            <Route path="/shared/:token" element={<SharedLocationPage />} />

            {/* ── Auth routes (no sidebar) ─────────────────────────────── */}
            <Route element={<AuthLayout />}>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/login/2fa" element={<TwoFactorVerifyPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
            </Route>

            {/* ── Authenticated app routes ─────────────────────────────── */}
            <Route element={<AppLayout />}>
              <Route index element={<DashboardPage />} />
              <Route path="/map" element={<LiveMapPage />} />

              {/* Tracking */}
              <Route path="/vehicles" element={<VehiclesPage />} />
              <Route path="/vehicles/:id" element={<VehicleDetailPage />} />
              <Route path="/vehicles/:id/history" element={<GpsHistoryPage />} />
              <Route path="/devices" element={<DevicesPage />} />
              <Route path="/sims" element={<SimsPage />} />
              <Route path="/drivers" element={<DriversPage />} />
              <Route path="/geofences" element={<GeofencesPage />} />
              <Route path="/history" element={<HistoryPage />} />
              <Route path="/trips" element={<TripsPage />} />
              <Route path="/trips/:id" element={<TripDetailPage />} />
              <Route path="/alerts" element={<AlertsPage />} />
              <Route path="/packets" element={<PacketInspectorPage />} />

              {/* School */}
              <Route path="/school/schools" element={<SchoolsPage />} />
              <Route path="/school/routes" element={<SchoolRoutesPage />} />
              <Route path="/school/students" element={<StudentsPage />} />
              <Route path="/school/attendance" element={<SchoolAttendancePage />} />

              {/* Field Force */}
              <Route path="/field-force/employees" element={<EmployeesPage />} />
              <Route path="/field-force/attendance" element={<FieldAttendancePage />} />
              <Route path="/field-force/tasks" element={<TasksPage />} />

              {/* Pets */}
              <Route path="/pets" element={<PetsPage />} />

              {/* EV */}
              <Route path="/ev/batteries" element={<BatteriesPage />} />
              <Route path="/ev/swaps" element={<BatterySwapsPage />} />
              <Route path="/ev/stations" element={<EVStationsPage />} />

              {/* Dispatch */}
              <Route path="/dispatch/bookings" element={<BookingsPage />} />
              <Route path="/dispatch/clients" element={<DispatchClientsPage />} />
              <Route path="/dispatch/service-types" element={<ServiceTypesPage />} />
              <Route path="/dispatch/promos" element={<PromosPage />} />
              <Route path="/dispatch/packages" element={<PackagesPage />} />

              {/* Billing */}
              <Route path="/billing/subscriptions"    element={<SubscriptionsPage />} />
              <Route path="/billing/my-subscription"  element={<MySubscriptionPage />} />
              <Route path="/billing/invoices"      element={<InvoicesPage />} />
              <Route path="/billing/payments"      element={<PaymentsPage />} />
              <Route path="/billing/wallet"        element={<WalletPage />} />
              <Route path="/billing/plans"         element={<PlansPage />} />
              <Route path="/billing/domain-plans"  element={<DomainPlansPage />} />
              <Route path="/billing/gateways"      element={<GatewaysPage />} />
              <Route path="/billing/profile"       element={<BillingProfilePage />} />
              <Route path="/billing/pdf-templates"     element={<PdfTemplatesPage />} />
              <Route path="/billing/tax-config"        element={<TaxConfigPage />} />
              <Route path="/billing/signing-cert"      element={<SigningCertPage />} />
              <Route path="/billing/platform-profile"  element={<PlatformProfilePage />} />
              <Route path="/billing/platform-gateways" element={<PlatformGatewaysPage />} />
              <Route path="/billing/customers"         element={<CustomerBillingPage />} />
              <Route path="/billing/dunning-rules"    element={<DunningRulesPage />} />

              {/* Reports */}
              <Route path="/reports" element={<ReportsPage />} />

              {/* Admin */}
              <Route path="/admin/users" element={<AdminUsersPage />} />
              <Route path="/admin/user-types" element={<UserTypesPage />} />
              <Route path="/admin/alert-types" element={<AlertTypesPage />} />
              <Route path="/admin/alert-rules" element={<AlertRulesPage />} />
              <Route path="/admin/notifications" element={<NotificationsPage />} />
              <Route path="/admin/payment-gateways" element={<PaymentGatewaysPage />} />
              <Route path="/admin/domain" element={<DomainSettingsPage />} />
              <Route path="/admin/vehicle-types" element={<VehicleTypesPage />} />
              <Route path="/admin/data-brokers" element={<DataBrokerPage />} />
              <Route path="/admin/api-keys"           element={<ApiKeysPage />} />
              <Route path="/admin/dashcam-config"     element={<DashcamConfigPage />} />
              <Route path="/admin/document-reminders" element={<DocumentRemindersPage />} />
              <Route path="/admin/calendars"          element={<CalendarsPage />} />

              {/* Transport */}
              <Route path="/transport/vendors"   element={<TransportVendorsPage />} />
              <Route path="/transport/employees" element={<TransportEmployeesPage />} />
              <Route path="/transport/routes"    element={<TransportRoutesPage />} />
              <Route path="/transport/rosters"   element={<TransportRostersPage />} />
              <Route path="/transport/sos"       element={<TransportSOSPage />} />

              {/* White Label */}
              <Route path="/white-label/domains"     element={<WhiteLabelDomainsPage />} />
              <Route path="/white-label/migrations"  element={<WhiteLabelMigrationsPage />} />

              {/* System — super admin only */}
              <Route path="/system" element={<SystemPage />} />
              <Route path="/system/parsers" element={<ParsersPage />} />

              {/* Profile */}
              <Route path="/profile" element={<ProfilePage />} />

              {/* Catch-all → dashboard */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ThemeProvider>
  )
}
